import { getNestedValue } from './field-accessor.util';
import { SortState } from '../interfaces/sort-state.interface';

/**
 * Utility functions for sorting data
 */

/**
 * Sort array of objects by a field path
 *
 * @param data - Array to sort
 * @param field - Field path to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @param comparator - Optional custom comparator function
 * @returns Sorted array (new array, original is not modified)
 */
export function sortByField<T>(
  data: T[],
  field: string,
  direction: 'asc' | 'desc' | '' = 'asc',
  comparator?: (a: any, b: any, isAscending: boolean) => number
): T[] {
  if (!data || data.length === 0 || !field || direction === '') {
    return [...data];
  }

  const isAscending = direction === 'asc';

  return [...data].sort((a, b) => {
    const valueA = getNestedValue(a, field);
    const valueB = getNestedValue(b, field);

    // Use custom comparator if provided
    if (comparator) {
      return comparator(valueA, valueB, isAscending);
    }

    // Use default comparator
    return defaultComparator(valueA, valueB, isAscending);
  });
}

/**
 * Sort array by multiple fields (multi-column sort)
 *
 * @param data - Array to sort
 * @param sortStates - Array of sort states (priority by index)
 * @param comparators - Map of custom comparators by field
 * @returns Sorted array (new array)
 */
export function sortByMultipleFields<T>(
  data: T[],
  sortStates: SortState<T>[],
  comparators?: Map<string, (a: any, b: any, isAscending: boolean) => number>
): T[] {
  if (!data || data.length === 0 || !sortStates || sortStates.length === 0) {
    return [...data];
  }

  // Filter out empty directions and sort by priority
  const activeSorts = sortStates
    .filter(s => s.direction && s.direction !== '' as any) 
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

  if (activeSorts.length === 0) {
    return [...data];
  }

  return [...data].sort((a, b) => {
    // Compare by each sort state in order
    for (const sortState of activeSorts) {
      const field = sortState.field as string;
      const valueA = getNestedValue(a, field);
      const valueB = getNestedValue(b, field);
      const isAscending = sortState.direction === 'asc';

      // Get custom comparator if exists
      const comparator = comparators?.get(field);

      const result = comparator
        ? comparator(valueA, valueB, isAscending)
        : defaultComparator(valueA, valueB, isAscending);

      // If values are different, return result
      // If values are equal, continue to next sort state
      if (result !== 0) {
        return result;
      }
    }

    return 0;
  });
}

/**
 * Default comparator function
 * Handles strings, numbers, dates, booleans, nulls, and undefined
 *
 * @param a - First value
 * @param b - Second value
 * @param isAscending - Sort direction
 * @returns Comparison result (-1, 0, 1)
 */
export function defaultComparator(a: any, b: any, isAscending: boolean): number {
  // Handle null and undefined
  if (a === null || a === undefined) {
    if (b === null || b === undefined) return 0;
    return isAscending ? -1 : 1;
  }

  if (b === null || b === undefined) {
    return isAscending ? 1 : -1;
  }

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return compareNumbers(a.getTime(), b.getTime(), isAscending);
  }

  // Try to parse as dates if strings
  if (typeof a === 'string' && typeof b === 'string') {
    const dateA = new Date(a);
    const dateB = new Date(b);
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return compareNumbers(dateA.getTime(), dateB.getTime(), isAscending);
    }
  }

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return compareNumbers(a, b, isAscending);
  }

  // Handle booleans
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return compareBooleans(a, b, isAscending);
  }

  // Handle strings (case-insensitive)
  return compareStrings(String(a), String(b), isAscending);
}

/**
 * Compare two numbers
 */
function compareNumbers(a: number, b: number, isAscending: boolean): number {
  if (a === b) return 0;
  const result = a < b ? -1 : 1;
  return isAscending ? result : -result;
}

/**
 * Compare two strings (case-insensitive)
 */
function compareStrings(a: string, b: string, isAscending: boolean): number {
  const result = a.toLowerCase().localeCompare(b.toLowerCase());
  return isAscending ? result : -result;
}

/**
 * Compare two booleans
 */
function compareBooleans(a: boolean, b: boolean, isAscending: boolean): number {
  if (a === b) return 0;
  const result = a ? 1 : -1;
  return isAscending ? result : -result;
}

/**
 * Case-sensitive string comparator
 */
export function caseSensitiveComparator(a: any, b: any, isAscending: boolean): number {
  if (a === null || a === undefined) {
    if (b === null || b === undefined) return 0;
    return isAscending ? -1 : 1;
  }

  if (b === null || b === undefined) {
    return isAscending ? 1 : -1;
  }

  const result = String(a).localeCompare(String(b));
  return isAscending ? result : -result;
}

/**
 * Numeric comparator (treats everything as numbers)
 */
export function numericComparator(a: any, b: any, isAscending: boolean): number {
  const numA = parseFloat(a);
  const numB = parseFloat(b);

  if (isNaN(numA)) {
    if (isNaN(numB)) return 0;
    return isAscending ? -1 : 1;
  }

  if (isNaN(numB)) {
    return isAscending ? 1 : -1;
  }

  return compareNumbers(numA, numB, isAscending);
}

/**
 * Date comparator
 */
export function dateComparator(a: any, b: any, isAscending: boolean): number {
  const dateA = a instanceof Date ? a : new Date(a);
  const dateB = b instanceof Date ? b : new Date(b);

  if (isNaN(dateA.getTime())) {
    if (isNaN(dateB.getTime())) return 0;
    return isAscending ? -1 : 1;
  }

  if (isNaN(dateB.getTime())) {
    return isAscending ? 1 : -1;
  }

  return compareNumbers(dateA.getTime(), dateB.getTime(), isAscending);
}

/**
 * Toggle sort direction
 * asc -> desc -> '' (no sort) -> asc
 */
export function toggleSortDirection(current: 'asc' | 'desc' | ''): 'asc' | 'desc' | '' {
  if (current === 'asc') return 'desc';
  if (current === 'desc') return '';
  return 'asc';
}

/**
 * Get opposite sort direction
 */
export function getOppositeSortDirection(direction: 'asc' | 'desc'): 'asc' | 'desc' {
  return direction === 'asc' ? 'desc' : 'asc';
}

/**
 * Check if data is sorted by field
 */
export function isSortedBy<T>(sortStates: SortState<T>[], field: string): boolean {
  return sortStates.some(s => s.field === field && s.direction !== '');
}

/**
 * Get sort direction for a field
 */
export function getSortDirection<T>(sortStates: SortState<T>[], field: string): 'asc' | 'desc' | '' {
  const sortState = sortStates.find(s => s.field === field);
  return sortState?.direction ?? '';
}

/**
 * Get sort priority for a field (used in multi-column sort)
 */
export function getSortPriority<T>(sortStates: SortState<T>[], field: string): number | undefined {
  const sortState = sortStates.find(s => s.field === field);
  return sortState?.priority;
}
